import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AuthenticateRequestDto, AuthenticationResponseDto } from "../models/auth.model";
import { environment } from "../../../../environments/environment";


/**
 * Auth API — pokriva /auth/* endpointe (login, social login, signup, login OTP,
 * refresh token). Candidate-portal pozivi (profil, prijave, CV, phone verify)
 * žive u CandidatePortalApiService.
 */
@Injectable({ providedIn: 'root' })
export class AuthApiService {
    private http: HttpClient = inject(HttpClient);
    private apiUrl = environment.apiBaseUrl;

    /**
     * POST /auth/authenticate — email login.
     * withCredentials:true → preglednik sprema HttpOnly refresh cookie
     * (koristi ga budući refresh-token task).
     */
    authenticate(body: AuthenticateRequestDto): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate`,
            body,
            { withCredentials: true },
        );
    }

    /**
     * POST /auth/authenticate/google — Google login.
     * idToken se šalje kao `Authorization: Bearer` header (NE u body) —
     * tako backend implementira /authenticate/google
     * (AuthenticationController#googleAuthenticate). Body ostaje prazan.
     */
    authenticateGoogle(idToken: string): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate/google`,
            {},
            {
                headers: { Authorization: `Bearer ${idToken}` },
                withCredentials: true,
            },
        );
    }

    /**
     * POST /auth/authenticate/facebook — Facebook login.
     * accessToken iz FB SDK-a se šalje kao `Authorization: Bearer` header
     * (NE u body) — backend ga čita iz Authorization headera i radi
     * substring(7) (AuthenticationController#facebookAuthenticate), identično
     * kao /authenticate/google. Body ostaje prazan.
     * withCredentials:true → preglednik sprema HttpOnly refresh cookie.
     */
    authenticateFacebook(accessToken: string): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/authenticate/facebook`,
            {},
            {
                headers: { Authorization: `Bearer ${accessToken}` },
                withCredentials: true,
            },
        );
    }

    refreshToken(): Observable<AuthenticationResponseDto> {
        return this.http.post<AuthenticationResponseDto>(
            `${this.apiUrl}/auth/refresh-token`,
            {},
            { withCredentials: true },
        );
    }

    /** POST /auth/logout — backend briše HttpOnly refresh cookie na ovom uređaju. */
    logout(): Observable<void> {
        return this.http.post<void>(`${this.apiUrl}/auth/logout`, {}, { withCredentials: true });
    }

    /** POST /auth/forgot-password — backend šalje link samo ako račun postoji (odgovor je uvijek isti). */
    zaboravljenaLozinka(email: string): Observable<unknown> {
        return this.http.post(`${this.apiUrl}/auth/forgot-password`, { email });
    }

    /** POST /auth/reset-password — 400 za istekao, iskorišten ili nepostojeći token. */
    resetLozinke(token: string, newPassword: string): Observable<unknown> {
        return this.http.post(`${this.apiUrl}/auth/reset-password`, { token, newPassword });
    }
}
